import type { MavisOpportunityRow } from '../../types/mavis';

function formatCompact(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
}

interface StatCardProps {
  label: string;
  value: string;
  sub: string;
  valueClass?: string;
}

function StatCard({ label, value, sub, valueClass = 'text-app-ink' }: StatCardProps) {
  return (
    <div className="bg-app-surface rounded-xl border border-app-line shadow-sm p-4">
      <p className="text-xs font-medium text-app-muted mb-1.5">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${valueClass}`}>{value}</p>
      <p className="text-xs text-app-muted mt-0.5">{sub}</p>
    </div>
  );
}

interface Props {
  opportunities: MavisOpportunityRow[];
}

export default function MavisPipelineStats({ opportunities }: Props) {
  const open = opportunities.filter(o => o.stage !== 'close_won' && o.stage !== 'close_lost');
  const totalPipeline = open.reduce((s, o) => s + (o.amount ?? 0), 0);
  const weighted = open.reduce(
    (s, o) => s + (o.amount ?? 0) * ((o.forecast_percentage ?? 0) / 100),
    0,
  );
  const commitReady = open.filter(o => o.forecast_label === 'commit_ready').length;
  const commitRisk  = open.filter(o => o.forecast_label === 'commit_risk').length;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      <StatCard
        label="Open Pipeline"
        value={totalPipeline > 0 ? formatCompact(totalPipeline) : '—'}
        sub={`${open.length} ${open.length === 1 ? 'opportunity' : 'opportunities'}`}
      />
      <StatCard
        label="Weighted Forecast"
        value={weighted > 0 ? formatCompact(weighted) : '—'}
        sub="by forecast %"
      />
      <StatCard
        label="Open Opportunities"
        value={String(open.length)}
        sub="active deals"
      />
      <StatCard
        label="Commit Ready"
        value={String(commitReady)}
        sub="opportunities"
        valueClass={commitReady > 0 ? 'text-forest-600' : 'text-app-ink'}
      />
      <StatCard
        label="Commit Risk"
        value={String(commitRisk)}
        sub="opportunities"
        valueClass={commitRisk > 0 ? 'text-red-600' : 'text-app-ink'}
      />
    </div>
  );
}
